import { Button, Card, Form, notification, Space, Table } from "antd";
import classNames from "classnames/bind";
import style from '../index.module.scss'
import { useTranslation } from "react-i18next";
import { formatCurrencyRevenue } from "features/revenue/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CloudUploadOutlined } from "@ant-design/icons";
import moment from "moment";
import revenueApi from "api/revenue";
import imageApi from "api/image";
import AdminModalImport from "./AdminModalImport";
import AdminTableExport from "./AdminTableExport";

const cx = classNames.bind(style)

export default function AdminRevenueTable({ shops: rawShops, month, listHoliday }) {
    const { t } = useTranslation()
    const [api, contextHolder] = notification.useNotification();

    const [formImport] = Form.useForm();
    const [loading, setLoading] = useState(false)
    const [shops, setShops] = useState(rawShops || [])
    const [openImport, setOpenImport] = useState(false)

    const notify = useCallback((type, message, description) => {
        api[type]({ message, description });
    }, [api]);

    // Sync shops from props
    useEffect(() => {
        if (rawShops) setShops(rawShops)
    }, [rawShops])

    const onOpenImport = () => setOpenImport(true)
    const closeModalImport = () => {
        setOpenImport(false)
        formImport.resetFields()
    }

    // Build dataSource — memoized per shops/month
    const dataSource = useMemo(() => {
        if (!shops?.length || !month) return [];
        const daysInMonth = moment(month, 'YYYY-MM').daysInMonth();
        return Array.from({ length: daysInMonth }, (_, i) => {
            const date = moment(month, 'YYYY-MM').date(i + 1).format('YYYY-MM-DD');
            const row = { key: date, date, index: i };
            shops.forEach(shop => {
                const found = shop.rows?.find(r => r.date === date);
                row[`${shop.shopId}__revenue`] = found?.revenue ?? null;
                row[`${shop.shopId}__wasteBoxShopPercent`] = found?.wasteBoxShopPercent ?? null;
                row[`${shop.shopId}__revenueCabinet1`] = shop.cabinet >= 1 ? (found?.revenueCabinet1 ?? null) : undefined;
                row[`${shop.shopId}__revenueCabinet2`] = shop.cabinet >= 2 ? (found?.revenueCabinet2 ?? null) : undefined;
            });
            return row;
        });
    }, [shops, month]);

    // Pre-compute holiday lookup set for fast O(1) check
    const holidaySet = useMemo(() => {
        return new Set(listHoliday.map(item => moment(item.date).format('YYYY-MM-DD')));
    }, [listHoliday]);

    // Summary — pre-compute sums per shop
    const summaryData = useMemo(() => {
        const sums = {};
        shops.forEach(shop => {
            sums[shop.shopId] = { revenue: 0, wasteBox: 0, cabinet1: 0, cabinet2: 0 };
        });
        dataSource.forEach(row => {
            shops.forEach(shop => {
                const r = row[`${shop.shopId}__revenue`];
                const wb = row[`${shop.shopId}__wasteBoxShopPercent`];
                const c1 = row[`${shop.shopId}__revenueCabinet1`];
                const c2 = row[`${shop.shopId}__revenueCabinet2`];
                if (typeof r === 'number') sums[shop.shopId].revenue += r;
                if (typeof wb === 'number') sums[shop.shopId].wasteBox += wb;
                if (typeof c1 === 'number') sums[shop.shopId].cabinet1 += c1;
                if (typeof c2 === 'number') sums[shop.shopId].cabinet2 += c2;
            });
        });
        return sums;
    }, [dataSource, shops]);

    // Build columns — memoized per shops (stability via shop IDs)
    const columns = useMemo(() => {
        if (!shops?.length) return [];

        const baseColumns = [
            {
                title: t('day'),
                dataIndex: 'index',
                key: 'index',
                width: 80,
                fixed: 'left',
                align: 'right',
                render: (_, __, idx) => idx + 1,
            },
            {
                title: t('dow'),
                dataIndex: 'date',
                key: 'date',
                width: 80,
                fixed: 'left',
                render: (val) => t(moment(val).format('dddd')),
            },
        ];

        const shopColumns = shops.flatMap(shop => {
            const groupKey = `shop_${shop.shopId}`;
            const fields = [];

            // Revenue (always present)
            fields.push({
                title: t('shop'),
                dataIndex: `${shop.shopId}__revenue`,
                key: `${shop.shopId}__revenue`,
                width: 100,
                align: 'right',
            });

            // WasteBoxShopPercent (always present)
            fields.push({
                title: t('%wasteBoxShop'),
                dataIndex: `${shop.shopId}__wasteBoxShopPercent`,
                key: `${shop.shopId}__wasteBoxShopPercent`,
                width: 90,
                align: 'right',
            });

            // Cabinet 1 (if cabinet >= 1)
            if (shop.cabinet >= 1) {
                fields.push({
                    title: t('cabinet', { value: 1 }),
                    dataIndex: `${shop.shopId}__revenueCabinet1`,
                    key: `${shop.shopId}__revenueCabinet1`,
                    width: 100,
                    align: 'right',
                });
            }

            // Cabinet 2 (if cabinet >= 2)
            if (shop.cabinet >= 2) {
                fields.push({
                    title: t('cabinet', { value: 2 }),
                    dataIndex: `${shop.shopId}__revenueCabinet2`,
                    key: `${shop.shopId}__revenueCabinet2`,
                    width: 100,
                    align: 'right',
                });
            }

            return {
                title: shop.shopCode,
                key: groupKey,
                children: fields,
            };
        });

        return [...baseColumns, ...shopColumns];
    }, [shops, t]);

    // Filter phantom children + attach shouldCellUpdate for performance
    const mergedColumns = useMemo(() => {
        return columns.map(col => {
            if (col.children) {
                return {
                    ...col,
                    children: col.children.map(child => ({
                        ...child,
                        shouldCellUpdate: () => false,
                        render: (val) => {
                            if (val == null) return '';
                            if (child.key?.includes('wasteBoxShopPercent')) {
                                return `${val}%`;
                            }
                            return formatCurrencyRevenue(val);
                        },
                    })),
                };
            }
            return col;
        });
    }, [columns]);

    // Row className memoized
    const getRowClassName = useCallback((record) => {
        if (!moment(record.date).weekday() || holidaySet.has(record.date)) {
            return cx('dayoff');
        }
        return '';
    }, [holidaySet]);

    // Import handler — upload to Cloudinary then send URL to BE (avoids 413 payload too large)
    const onImport = useCallback(async (values) => {
        try {
            setLoading(true);
            const file = values.upload[0].originFileObj;

            // Step 1: Upload file to Cloudinary via BE endpoint
            const formData = new FormData();
            formData.append('file', file);
            const uploadRes = await imageApi.uploadImage(formData);
            const fileUrl = uploadRes.result.secure_url;

            // Step 2: Send URL + month to BE — BE downloads, parses, upserts
            await revenueApi.processMultiShopRevenueFromUrl({ url: fileUrl, month });

            notify("success", t('notification'), t('importSuccess'));
            window.location.reload();
        } catch (error) {
            notify("error", t('error'), error?.response?.data?.message || t('hasError'));
            setLoading(false);
        }
    }, [month, notify, t]);

    return (
        <>
            {contextHolder}
            <AdminModalImport
                loading={loading}
                show={openImport}
                onFinish={onImport}
                form={formImport}
                onCancel={closeModalImport}
            />
            <Card>
                <div className={cx('button-area')} style={{ marginBottom: 24 }}>
                    <Space>
                        <Button size="large" icon={<CloudUploadOutlined />} onClick={onOpenImport}>
                            {t('import')}
                        </Button>
                        <AdminTableExport {...{ month, shops, dataSource }} />
                    </Space>
                </div>

                <Table
                    size="small"
                    columns={mergedColumns}
                    dataSource={dataSource}
                    rowKey="key"
                    bordered
                    pagination={false}
                    sticky
                    scroll={{ x: 'max-content', y: 480 }}
                    rowClassName={getRowClassName}
                    virtual
                    summary={() => (
                        <Table.Summary fixed='bottom'>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0}><strong>{t('sum')}</strong></Table.Summary.Cell>
                                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                                {shops.flatMap(shop => {
                                    const sums = summaryData[shop.shopId] || {};
                                    const cells = [
                                        <Table.Summary.Cell key={`${shop.shopId}__revenue`} align="right">
                                            <strong>{formatCurrencyRevenue(sums.revenue)}</strong>
                                        </Table.Summary.Cell>,
                                        <Table.Summary.Cell key={`${shop.shopId}__wasteBoxShopPercent`} align="right">
                                            <strong>{sums.wasteBox ? `${sums.wasteBox}%` : ''}</strong>
                                        </Table.Summary.Cell>,
                                    ];
                                    if (shop.cabinet >= 1) {
                                        cells.push(
                                            <Table.Summary.Cell key={`${shop.shopId}__revenueCabinet1`} align="right">
                                                <strong>{formatCurrencyRevenue(sums.cabinet1)}</strong>
                                            </Table.Summary.Cell>
                                        );
                                    }
                                    if (shop.cabinet >= 2) {
                                        cells.push(
                                            <Table.Summary.Cell key={`${shop.shopId}__revenueCabinet2`} align="right">
                                                <strong>{formatCurrencyRevenue(sums.cabinet2)}</strong>
                                            </Table.Summary.Cell>
                                        );
                                    }
                                    return cells;
                                })}
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            </Card>
        </>
    );
}
